/**
 * Text-to-Speech Service
 * 
 * Uses OpenAI's TTS API with the ONYX voice for medical content playback.
 * Works cross-platform including iOS Safari.
 * Provides audio playback controls and status callbacks.
 * 
 * iOS Safari strategy:
 *   During the first user gesture (e.g., "Start" button), we create and unlock
 *   a persistent Audio element AND AudioContext. For all subsequent TTS plays,
 *   we REUSE that same Audio element (changing its src). This preserves the
 *   user-gesture "unlock" so audio plays even from timer/callback contexts.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

// TTS Configuration
const TTS_CONFIG = {
  model: 'gpt-4o-mini-tts',
  voice: 'onyx', // Deep, professional male voice
  speed: 0.95, // Slightly slower for medical clarity (0.25 to 4.0)
};

// Audio player instances
let currentAudio = null;
let persistentAudioElement = null; // Reusable Audio element unlocked during user gesture
let isPlaying = false;
let audioContextUnlocked = false;
let persistentAudioContext = null; // Keep AudioContext alive for iOS
let currentObjectUrl = null; // Track object URL for cleanup
/** Promise for the initial unlock play(); we wait for this before loading new src to avoid "play() interrupted by new load request" */
let persistentElementUnlockPromise = null;
/** Monotonic token so stale HTMLAudio events cannot complete a newer playback. */
let activePlaybackToken = 0;
/** Rejects the in-flight playAudio promise when stopSpeaking() interrupts playback (barge-in). */
let rejectActivePlay = null;

export const TTS_PLAYBACK_STOPPED_ERROR = 'playback_stopped';

// Safety timeout for play operations (ms)
// If audio doesn't start playing within this time, consider it failed
const PLAY_TIMEOUT_MS = 5000;
const UNLOCK_WAIT_MS = 1500; // Max wait for unlock play to settle before reusing element

/**
 * After HTMLMediaElement `ended`, wait this long before signaling completion.
 * Browsers often fire `ended` slightly before the device finishes the audible tail;
 * resuming mic / getUserMedia right away can duck or truncate the last phrase
 * (e.g. symptom checker closing line).
 */
export const TTS_PLAYBACK_POST_END_PAD_MS = 320;

/** If `ended` fires while currentTime is still short of duration, wait for the real tail (HTMLAudio + MP3 quirks). */
export const TTS_AUDIO_TAIL_EPSILON_SEC = 0.48;
export const TTS_PREMATURE_TAIL_POLL_MS = 120;
export const TTS_PREMATURE_TAIL_MAX_EXTRA_MS = 45_000;

/** Timer + completion runner for post-`ended` pad (cleared by stopAudio / next play). */
let postEndPadTimerId = null;
let completePostEndPlay = null;
/** Interval when we detect a premature `ended` on HTMLAudioElement (symptom checker TTS cut-off). */
let tailRecoveryPollId = null;

function clearTailRecoveryPoll() {
  if (tailRecoveryPollId !== null) {
    clearInterval(tailRecoveryPollId);
    tailRecoveryPollId = null;
  }
}

const clearPostEndPad = (runCompletion) => {
  clearTailRecoveryPoll();
  if (postEndPadTimerId) {
    clearTimeout(postEndPadTimerId);
    postEndPadTimerId = null;
  }
  if (runCompletion && typeof completePostEndPlay === 'function') {
    const finish = completePostEndPlay;
    completePostEndPlay = null;
    finish();
  } else {
    completePostEndPlay = null;
  }
};

/**
 * Unlock audio context AND Audio element for autoplay (call on user gesture).
 * 
 * CRITICAL for iOS Safari: This must be called from a direct user gesture handler
 * (e.g., button onClick). It creates persistent audio resources that can be reused
 * for all subsequent TTS plays, even those triggered from timers or callbacks.
 */
export const unlockAudioContext = () => {
  try {
    // === AudioContext unlock ===
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass) {
      if (!persistentAudioContext) {
        persistentAudioContext = new AudioContextClass();
      }
      
      if (persistentAudioContext.state === 'suspended') {
        persistentAudioContext.resume().then(() => {
        }).catch(e => {
          console.warn('[ttsService] AudioContext resume failed:', e);
        });
      }
      
      if (!audioContextUnlocked) {
        const buffer = persistentAudioContext.createBuffer(1, 1, 22050);
        const source = persistentAudioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(persistentAudioContext.destination);
        source.start(0);
        audioContextUnlocked = true;
      }
    }
    
    // === Persistent Audio element unlock ===
    // On iOS Safari, Audio elements created outside user gesture context cannot play.
    // By creating and playing (a silent source) during the user gesture, we "unlock"
    // this element. We then REUSE it for all subsequent TTS by changing its src.
    if (!persistentAudioElement) {
      persistentAudioElement = new Audio();
      // Minimal silent WAV: 44-byte header + 0 data bytes
      const silentWav = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
      persistentAudioElement.src = silentWav;
      persistentAudioElement.volume = 1.0;
      const playPromise = persistentAudioElement.play();
      if (playPromise) {
        persistentElementUnlockPromise = playPromise
          .then(() => {
            persistentAudioElement.pause();
            persistentAudioElement.currentTime = 0;
          })
          .catch(e => {
            console.warn('[ttsService] Could not unlock persistent Audio element:', e.message);
          })
          .finally(() => {
            persistentElementUnlockPromise = null;
          });
      }
    }
    
    // === SpeechSynthesis unlock ===
    // iOS Safari also needs speechSynthesis "warmed up" during a user gesture
    // for reliable fallback TTS later.
    if ('speechSynthesis' in window) {
      try {
        const silentUtterance = new SpeechSynthesisUtterance('');
        silentUtterance.volume = 0;
        silentUtterance.rate = 1;
        window.speechSynthesis.speak(silentUtterance);
      } catch (e) {
        console.warn('[ttsService] SpeechSynthesis warmup failed:', e);
      }
    }
  } catch (e) {
    console.warn('[ttsService] Could not unlock audio context:', e);
  }
};

/**
 * Ensure audio is ready to play (call before each play attempt).
 * Resumes a suspended AudioContext which is common on iOS after
 * the browser suspends the audio session (e.g., after speech recognition).
 */
const ensureAudioReady = async () => {
  if (persistentAudioContext && persistentAudioContext.state === 'suspended') {
    try {
      await persistentAudioContext.resume();
    } catch (e) {
      console.warn('[ttsService] Could not resume AudioContext:', e);
    }
  }
};

/**
 * Convert base64 to Blob
 */
const base64ToBlob = (base64, mimeType = 'audio/mp3') => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};

/**
 * Generate speech from text using OpenAI TTS via Firebase Function
 * 
 * @param {string} text - Text to convert to speech
 * @param {string} language - Target language (affects pronunciation)
 * @param {Object} [options] - Per-call overrides
 * @param {string} [options.model] - TTS model override (e.g. 'tts-1' for the legacy model).
 *                                   Defaults to TTS_CONFIG.model ('gpt-4o-mini-tts').
 * @param {string} [options.voice] - Voice override (default TTS_CONFIG.voice).
 * @param {number} [options.speed] - Speed override (default TTS_CONFIG.speed).
 * @param {string} [options.instructions] - Tone hint (only honored by gpt-4o-mini-tts).
 * @returns {Promise<Blob>} - Audio blob
 */
const generateSpeech = async (text, language = 'en-US', options = {}) => {
  try {
    
    const functions = getFunctions();
    const chartmindTTS = httpsCallable(functions, 'chartmindTTS');
    
    const result = await chartmindTTS({
      text,
      voice: options.voice || TTS_CONFIG.voice,
      model: options.model || TTS_CONFIG.model,
      speed: options.speed != null ? options.speed : TTS_CONFIG.speed,
      ...(options.instructions ? { instructions: options.instructions } : {}),
      language,
    });
    
    
    if (!result.data?.audio) {
      throw new Error('No audio data returned from Firebase function');
    }
    
    // Convert base64 audio to Blob
    const audioBlob = base64ToBlob(result.data.audio, 'audio/mp3');
    return audioBlob;
  } catch (error) {
    console.error('[ttsService] Speech generation error:', error);
    throw error;
  }
};

/**
 * Play audio blob through the device speaker.
 * 
 * CRITICAL iOS strategy: If a persistent Audio element exists (unlocked during
 * user gesture), we REUSE it by changing its src. This allows playback even
 * when triggered from timers/callbacks (no fresh user gesture needed).
 * 
 * @param {Blob} audioBlob - Audio data to play
 * @param {Object} callbacks - Event callbacks
 * @param {Function} callbacks.onStart - Called when playback starts
 * @param {Function} callbacks.onEnd - Called when playback ends
 * @param {Function} callbacks.onError - Called on playback error
 * @returns {Promise<void>}
 */
const playAudio = (audioBlob, callbacks = {}) => {
  return new Promise((resolve, reject) => {
    // Async work is wrapped in an IIFE so the Promise executor itself stays
    // synchronous (avoids no-async-promise-executor); rejections propagate via .catch.
    const runPlayback = async () => {
    // Stop any currently playing audio (also completes any prior clip waiting in post-end pad)
    stopAudio();
    rejectActivePlay = reject;
    const playbackToken = ++activePlaybackToken;

    // Wait for initial unlock play() to settle before reusing the persistent element.
    // Otherwise setting .src and .load() interrupts that play() and triggers
    // "The play() request was interrupted by a new load request".
    if (persistentAudioElement && persistentElementUnlockPromise) {
      try {
        await Promise.race([
          persistentElementUnlockPromise,
          new Promise((_, rej) => setTimeout(() => rej(new Error('unlock_timeout')), UNLOCK_WAIT_MS)),
        ]);
      } catch (e) {
        if (e?.message !== 'unlock_timeout') {
          // ignore
        }
        persistentElementUnlockPromise = null;
      }
    }

    // Resume AudioContext before playing (critical for iOS)
    await ensureAudioReady();

    // Clean up previous object URL
    if (currentObjectUrl) {
      URL.revokeObjectURL(currentObjectUrl);
      currentObjectUrl = null;
    }

    const audioUrl = URL.createObjectURL(audioBlob);
    currentObjectUrl = audioUrl;

    // CRITICAL for iOS: Reuse the persistent Audio element if available.
    // On iOS Safari, new Audio() instances created outside user gesture context
    // cannot play(). But an Audio element that was play()-ed during a user gesture
    // can be reused indefinitely by changing its src.
    if (persistentAudioElement) {
      currentAudio = persistentAudioElement;
    } else {
      currentAudio = new Audio();
    }

    // Safety timeout - if audio doesn't fire onplay within PLAY_TIMEOUT_MS, consider it failed
    let playStarted = false;
    let settled = false;
    
    const cleanup = () => {
      // Don't destroy the persistent element - just clean up state
      if (currentAudio && currentAudio !== persistentAudioElement) {
        currentAudio = null;
      }
      isPlaying = false;
    };
    
    const safetyTimeout = setTimeout(() => {
      if (!playStarted && !settled) {
        settled = true;
        clearPostEndPad(false);
        cleanup();
        const timeoutError = new Error('Audio play timeout - no playback started within ' + PLAY_TIMEOUT_MS + 'ms');
        console.error('[ttsService]', timeoutError.message);
        callbacks.onError?.(timeoutError);
        reject(timeoutError);
      }
    }, PLAY_TIMEOUT_MS);
    
    // Set up event handlers (replaces any previous handlers on reused element)
    currentAudio.onplay = () => {
      if (playbackToken !== activePlaybackToken) return;
      playStarted = true;
      isPlaying = true;
      callbacks.onStart?.();
    };
    
    currentAudio.onended = () => {
      if (playbackToken !== activePlaybackToken) return;
      if (settled) return;
      clearTimeout(safetyTimeout);
      const runCompletion = () => {
        if (playbackToken !== activePlaybackToken) return;
        if (settled) return;
        settled = true;
        if (postEndPadTimerId) {
          clearTimeout(postEndPadTimerId);
          postEndPadTimerId = null;
        }
        completePostEndPlay = null;
        clearTailRecoveryPoll();
        cleanup();
        callbacks.onEnd?.();
        rejectActivePlay = null;
        resolve();
      };

      const queuePostEndPad = () => {
        completePostEndPlay = runCompletion;
        postEndPadTimerId = setTimeout(runCompletion, TTS_PLAYBACK_POST_END_PAD_MS);
      };

      const el = currentAudio;
      const dur = el?.duration;
      const ct = el?.currentTime;
      const eps = TTS_AUDIO_TAIL_EPSILON_SEC;
      const looksTruncated =
        el &&
        Number.isFinite(dur) &&
        dur > eps &&
        Number.isFinite(ct) &&
        ct < dur - eps;

      if (looksTruncated) {
        console.warn('[ttsService] HTMLAudio ended before duration tail; recovering playback', {
          duration: dur,
          currentTime: ct,
        });
        clearTailRecoveryPoll();
        void el.play().catch(() => {});
        const started = Date.now();
        const rate = Math.max(0.25, el.playbackRate || 1);
        const maxWaitMs = Math.min(
          TTS_PREMATURE_TAIL_MAX_EXTRA_MS,
          ((dur - ct) / rate) * 1000 + 2500
        );
        tailRecoveryPollId = window.setInterval(() => {
          if (settled) {
            clearTailRecoveryPoll();
            return;
          }
          if (currentAudio !== el) {
            clearTailRecoveryPoll();
            queuePostEndPad();
            return;
          }
          const ct2 = el.currentTime;
          const dur2 = el.duration;
          if (
            !Number.isFinite(dur2) ||
            dur2 <= eps ||
            (Number.isFinite(ct2) && ct2 >= dur2 - eps)
          ) {
            clearTailRecoveryPoll();
            queuePostEndPad();
            return;
          }
          if (Date.now() - started > maxWaitMs) {
            console.warn('[ttsService] Tail recovery timed out; completing TTS', {
              currentTime: ct2,
              duration: dur2,
            });
            clearTailRecoveryPoll();
            queuePostEndPad();
          }
        }, TTS_PREMATURE_TAIL_POLL_MS);
        return;
      }

      queuePostEndPad();
    };
    
    currentAudio.onerror = (error) => {
      if (playbackToken !== activePlaybackToken) return;
      if (settled) return;
      settled = true;
      clearTimeout(safetyTimeout);
      clearPostEndPad(false);
      cleanup();
      console.error('[ttsService] Playback error:', error);
      callbacks.onError?.(error);
      rejectActivePlay = null;
      reject(error);
    };
    
    // Set the source and start playback
    currentAudio.src = audioUrl;
    currentAudio.load(); // Explicitly load the new source
    
    try {
      await currentAudio.play();
    } catch (error) {
      if (playbackToken !== activePlaybackToken) return;
      console.error('[ttsService] Play failed (autoplay blocked?):', error.message);
      
      // Resume AudioContext and retry once
      await ensureAudioReady();
      
      try {
        await new Promise(r => setTimeout(r, 100));
        await currentAudio.play();
      } catch (retryError) {
        if (playbackToken !== activePlaybackToken) return;
        if (settled) return;
        settled = true;
        clearTimeout(safetyTimeout);
        clearPostEndPad(false);
        cleanup();
        console.error('[ttsService] Retry play also failed:', retryError.message);
        callbacks.onError?.(retryError);
        reject(retryError);
      }
    }
    };
    runPlayback().catch(reject);
  });
};

/**
 * Stop currently playing audio
 */
const stopAudio = () => {
  activePlaybackToken += 1;
  clearPostEndPad(true);
  if (rejectActivePlay) {
    const reject = rejectActivePlay;
    rejectActivePlay = null;
    reject(new Error(TTS_PLAYBACK_STOPPED_ERROR));
  }
  if (currentAudio) {
    try {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    } catch (e) {
      // Ignore errors on pause (element might be in bad state)
    }
    // Don't null out the persistent element - we want to reuse it
    if (currentAudio !== persistentAudioElement) {
      currentAudio = null;
    }
    isPlaying = false;
  }
};

/**
 * Prefetch speech audio without playing it
 * Use this to pre-generate audio that will be played later
 * 
 * @param {string} text - Text to convert to speech
 * @param {string} language - Target language
 * @param {Object} [options] - Per-call overrides (see generateSpeech): { model, voice, speed, instructions }
 * @returns {Promise<Blob>} - Audio blob ready for playback
 */
export const prefetchSpeech = async (text, language = 'en-US', options = {}) => {
  if (!text || text.trim().length === 0) {
    return null;
  }
  
  try {
    const audioBlob = await generateSpeech(text, language, options);
    return audioBlob;
  } catch (error) {
    console.error('[ttsService] Prefetch error:', error);
    return null;
  }
};

/**
 * Play a prefetched audio blob
 * 
 * @param {Blob} audioBlob - Prefetched audio blob
 * @param {Object} callbacks - Event callbacks
 * @returns {Promise<void>}
 */
export const playPrefetchedAudio = async (audioBlob, callbacks = {}) => {
  if (!audioBlob) {
    console.warn('[ttsService] No prefetched audio to play');
    callbacks.onError?.(new Error('No prefetched audio'));
    return;
  }
  
  try {
    await playAudio(audioBlob, callbacks);
  } catch (error) {
    console.error('[ttsService] Play prefetched error:', error);
    callbacks.onError?.(error);
    throw error;
  }
};

/**
 * Speak text with TTS (generate + play in one call)
 * 
 * @param {string} text - Text to speak
 * @param {string} language - Target language
 * @param {Object} callbacks - Event callbacks
 * @returns {Promise<void>}
 */
export const speak = async (text, language = 'en-US', callbacks = {}) => {
  if (!text || text.trim().length === 0) {
    return;
  }
  
  let audioGenerated = false;
  
  try {
    
    // Generate audio
    const audioBlob = await generateSpeech(text, language);
    audioGenerated = true;
    
    // Play audio (playAudio calls callbacks.onStart/onEnd/onError directly)
    await playAudio(audioBlob, callbacks);
    
  } catch (error) {
    console.error('[ttsService] Speak error:', error);
    // IMPORTANT: Only call onError here if generateSpeech failed.
    // If playAudio failed, it already called callbacks.onError before rejecting.
    // Calling it again would cause duplicate error handling upstream.
    if (!audioGenerated) {
      callbacks.onError?.(error);
    }
    throw error;
  }
};

/**
 * Check if audio is currently playing
 * @returns {boolean}
 */
export const isSpeaking = () => isPlaying;

/**
 * Stop any currently playing speech
 */
export const stopSpeaking = stopAudio;

/**
 * Get available voices (for future expansion)
 */
export const getAvailableVoices = () => [
  { id: 'onyx', name: 'Onyx', description: 'Deep, professional male voice' },
  { id: 'alloy', name: 'Alloy', description: 'Neutral, balanced voice' },
  { id: 'echo', name: 'Echo', description: 'Warm male voice' },
  { id: 'fable', name: 'Fable', description: 'British accent' },
  { id: 'nova', name: 'Nova', description: 'Friendly female voice' },
  { id: 'shimmer', name: 'Shimmer', description: 'Clear female voice' },
];

/**
 * Set TTS configuration
 */
export const setConfig = (config) => {
  if (config.voice) TTS_CONFIG.voice = config.voice;
  if (config.model) TTS_CONFIG.model = config.model;
  if (config.speed) TTS_CONFIG.speed = config.speed;
};

/**
 * Get current TTS configuration
 */
export const getConfig = () => ({ ...TTS_CONFIG });

/**
 * Warm the chartmindTTS Cloud Function (spin up an instance + init the OpenAI client) so the first
 * real prefetchSpeech of a session does not pay the cold-start penalty. Fire-and-forget; never throws.
 * Relies on the backend's `warm` branch, which returns without generating (billable) audio.
 * @returns {Promise<void>}
 */
export const warmUp = async () => {
  try {
    const functions = getFunctions();
    const chartmindTTS = httpsCallable(functions, 'chartmindTTS');
    await chartmindTTS({ warm: true });
  } catch (e) {
    // Best-effort: a failed warm just means the first real call pays the cold start.
    console.warn('[ttsService] warmUp failed (non-fatal):', e?.message);
  }
};

const ttsService = {
  speak,
  prefetchSpeech,
  playPrefetchedAudio,
  isSpeaking,
  stopSpeaking,
  getAvailableVoices,
  setConfig,
  getConfig,
  warmUp,
};

export default ttsService;
